import { Capacitor } from '@capacitor/core';
import dayjs from 'dayjs';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getRiderMapByIds } from '../../apis/riders';
import { updateOrderDoc } from '../../apis/updateOrderDoc';
import db_config from '../../configs/db_config';
import { ROLES } from '../../consts/language';
import { usePrinterContext } from '../../context/PrinterContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import usePrinter from '../../hooks/usePrinter';
import { BillStatus, DamagedItemStatus, OrderItemStatus } from '../../interfaces/index.types';
import { db } from '../../libs/firebase';
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, where } from '../../libs/firestore-wrapper';
import { getAllOrders, getCurrentSettings, getStadiumConfig } from '../../modals/order';
import { IOrders, OrderType } from '../../pages/Dashboard/order.types';
import { ISettings, IStadium } from '../../pages/Settings/settings.types';
import { fetchMultipleSeatsByTableNumbers } from '../../utils';
import Auth from '../../libs/auth-wrapper';
import { getEndOfDay, getStartOfDay } from '../../utils/handleDateTime';
import { formatSeatInfo, getSeatInformation } from '../../utils/seatHelpers';
import { useAuthContext } from '../AuthContext/AuthContext';
import { useGameScheduleContext } from '../GameScheduleContext';
import { NewOrderAlertsContext } from '../NewOrderAlerts';
import { useSettingsContext } from '../SettingsContext';

interface OrderContextValue {
  allOrders: IOrders[];
  allOrdersOfDay: IOrders[];
  newOrdersOfDay: IOrders[];
  readyOrdersOfDay: IOrders[];
  deliveredOrdersOfDay: IOrders[];
  deliveringOrdersOfDay: IOrders[];
  cancelledOrdersOfDay: IOrders[];
  allPreOrders: IOrders[];
  futurePreOrders: IOrders[];
  preOrdersOfDay: IOrders[];
  loading: boolean;
  updateOrderStatusDamaged: (orderId: string, status: string) => any;
  riderMap: Record<string, string>;
  riderInfos?: Record<string, { name: string; image?: string }>;
  seatDataMap: Map<string, any>;
}
export type Message = { isUnread: boolean; id: string };

const OrderContext = createContext<OrderContextValue>({
  allOrders: [],
  allOrdersOfDay: [],
  newOrdersOfDay: [],
  readyOrdersOfDay: [],
  deliveredOrdersOfDay: [],
  deliveringOrdersOfDay: [],
  cancelledOrdersOfDay: [],
  allPreOrders: [],
  futurePreOrders: [],
  preOrdersOfDay: [],
  loading: false,
  updateOrderStatusDamaged: (orderId, status) => {},
  riderMap: {},
  seatDataMap: new Map(),
});

const getOrderStatusForRestaurant = (order: IOrders, restaurant_id: string, enabledNewVersion: boolean): string => {
  if (!enabledNewVersion) return order.order_status;

  const restaurantOrder = order.multi_orders?.find((item) => item.restaurant_id === restaurant_id);
  return restaurantOrder?.status || order.order_status;
};

const filterOrdersByStatus = (statuses: string[], list: IOrders[], restaurant_id: string) =>
  list.filter((item) => {
    const statusMatched = statuses.includes(item?.order_status);
    const multiOrderStatusMatched = item?.multi_orders?.some((mo) => mo.restaurant_id === restaurant_id && statuses.includes(mo.status));
    return (statusMatched || multiOrderStatusMatched) && item?.foods?.length > 0;
  });

const isFuturePreOrder = (order: Partial<IOrders>) => {
  if (!order?.is_pre_order || !order.requested_delivery_time?.seconds) return false;
  const deliveryDate = dayjs(order.requested_delivery_time.seconds * 1000).format('YYYY-MM-DD');
  const today = dayjs().format('YYYY-MM-DD');
  return deliveryDate > today;
};

const logOrderQueryError = async (label: string, error: any) => {
  const user = await Auth.getCurrentUser().catch(() => null);
  if (user) console.error(label, error);
};

const OrderProvider = ({ children }: { children: ReactNode }) => {
  const damagedPrefix = '_DAMAGED';
  const [loading, setLoading] = useState(true);
  let lastCreateTime = useRef(0);
  let lastPromotionCreateTime = useRef(0);
  let lastAstrolabCreateTime = useRef(0);
  const [orderList, setOrderList] = useState<IOrders[]>([]);

  const [orderPromotionList, setOrderPromotionList] = useState<IOrders[]>([]);
  const [astrolabOrderList, setAstrolabOrderList] = useState<IOrders[]>([]);
  let { showNewOrderAlert, isNotificationEnabled, setNotifications } = useContext(NewOrderAlertsContext);
  const { addToQueue } = usePrinter();
  const printerContext = usePrinterContext();
  const isEnable = useRef(isNotificationEnabled);
  const addToQueueRef = useRef<typeof addToQueue | null>(null);
  const printerContextRef = useRef(printerContext);
  const unsubscribeOrdersRef = useRef<(() => void) | null>(null);
  const unsubscribePromotionOrdersRef = useRef<(() => void) | null>(null);
  const ordersByRestaurantId = useRef<Map<string, IOrders>>(new Map());
  const ordersByRestaurantIds = useRef<Map<string, IOrders>>(new Map());
  const astrolabOrdersByRestaurantId = useRef<Map<string, IOrders>>(new Map());
  const astrolabOrdersByRestaurantIds = useRef<Map<string, IOrders>>(new Map());
  const riderMap = useRef<Record<string, string>>({});
  const [riderInfos, setRiderInfos] = useState<Record<string, { name: string; image?: string }>>({});
  const { stadiumData: stadium_data } = useSettingsContext();

  const fetchAndStoreRiderInfo = async (ids: string[]) => {
    if (!ids || ids.length === 0) return;
    try {
      const newMap = await getRiderMapByIds(ids);
      riderMap.current = { ...riderMap.current, ...newMap };

      const infoMap: Record<string, { name: string; image?: string }> = {};
      const rid = restaurantId || adminRestaurantId;
      if (!rid) return;

      const configSnapshot = await getDoc(getCurrentSettings(rid));
      const dataSettings = configSnapshot?.data() as ISettings | undefined;
      if (!dataSettings || !dataSettings.stadium_id) return;
      const stadiumSnapshot = await getDoc(doc(getStadiumConfig(), dataSettings.stadium_id));
      const configData = stadiumSnapshot?.data();

      for (const id of ids) {
        try {
          const userSnap = await getDoc(doc(db.collection(db_config.DB_TABLE_STADIUM_MANAGE_USERS), id));
          if (userSnap && userSnap.exists()) {
            const u = userSnap.data();
            const gender = u?.gender ?? 0;
            const imageUrl = configData?.rider_url?.[gender] ?? null;
            infoMap[id] = { name: newMap[id] || u?.user_name || '', image: imageUrl };
          } else {
            infoMap[id] = { name: newMap[id] || '' };
          }
        } catch (e) {
          infoMap[id] = { name: newMap[id] || '' };
        }
      }

      setRiderInfos((prev) => ({ ...prev, ...infoMap }));
    } catch (e) {
      // ignore name map errors
    }
  };
  const [seatDataMap, setSeatDataMap] = useState<Map<string, any>>(new Map());
  const { restaurantId } = usePermission();
  const { userInfo } = useAuthContext();
  const { enabledNewVersion } = usePreorderEnabled();

  const adminRestaurantId = userInfo?.restaurants?.find((restaurant: any) => restaurant.is_admin_store)?.id;
  const isSuperAdmin = userInfo?.role === ROLES.SUPER_ADMIN;
  const isStaff = userInfo?.role === ROLES.STAFF;

  useEffect(() => {
    isEnable.current = isNotificationEnabled;
  }, [isNotificationEnabled]);

  useEffect(() => {
    addToQueueRef.current = addToQueue;
  }, [addToQueue]);

  useEffect(() => {
    printerContextRef.current = printerContext;
  }, [printerContext]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (restaurantId) {
        await handleGetAllOrdersPromotion();
        await handleGetAllOrders();
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (isSuperAdmin && adminRestaurantId) {
      handleGetAstrolabNotifications();
    }
  }, [adminRestaurantId, isSuperAdmin]);

  // The list shown on screen is always derived from both branch stores at the moment it is published, never from a
  // copy taken earlier: the two listeners (restaurant_id / restaurant_ids) deliver independently and their handlers
  // await before publishing, so a handler that publishes late must not overwrite what the other one already merged.
  // An order matching both queries is listed once.
  const getMergedOrders = (): IOrders[] => {
    const merged = new Map<string, IOrders>([...Array.from(ordersByRestaurantId.current), ...Array.from(ordersByRestaurantIds.current)]);
    return Array.from(merged.values()).sort((a, b) => b.order_create_time.seconds - a.order_create_time.seconds);
  };

  const handleOrdersBranchSnapshot = async (querySnapshot: any, storeRef: React.MutableRefObject<Map<string, IOrders>>) => {
    // Apply the snapshot to this branch's store before any await so snapshots of one listener land in arrival order.
    querySnapshot.docChanges().forEach((change: any) => {
      if (change.type === 'removed') {
        storeRef.current.delete(change.doc.id);
        return;
      }

      const data = change.doc.data();
      storeRef.current.set(change.doc.id, {
        ...data,
        all_foods: data?.foods || [],
        foods: data?.foods?.filter((food: any) => food.restaurantId === restaurantId || data?.restaurant_id === restaurantId) || [],
        id: change.doc.id,
        type: 'normal',
        order_type: data.order_type || OrderType.REGULAR_ORDERS,
        is_pre_order: data.is_pre_order || false,
      });
    });

    if (!querySnapshot.empty) {
      const settings = await getDoc(getCurrentSettings(restaurantId));
      const dataSettings = settings.data() as ISettings;
      const stadium = await getDoc(doc(getStadiumConfig(), dataSettings.stadium_id));
      const stadiumData = stadium.data() as IStadium;

      querySnapshot.docChanges().forEach((change: any) => {
        const data = change.doc.data();

        const isAddedOrder = change.type === 'added';
        const isPreparingOrder = data.order_status === OrderItemStatus.PREPARING;
        const isDamagedCreatedOrder = data.order_status === DamagedItemStatus.DAMAGED_CREATED;
        const isCompletePayment = data.bill_status === BillStatus.CLEARED;
        const isLastCreateTimeValid = lastCreateTime.current && lastCreateTime.current < data.order_create_time.seconds;
        const isEnableFalse = isEnable.current;
        const shouldSkipImmediateAlert = isFuturePreOrder(data);
        if (isAddedOrder && isDamagedCreatedOrder && isLastCreateTimeValid && isStaff && !shouldSkipImmediateAlert) {
          const orderId = change.doc.id;
          if (isEnableFalse) {
            showNewOrderAlert(orderId);
            setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
          }
        }
        if (isAddedOrder && isPreparingOrder && isLastCreateTimeValid && isCompletePayment && isStaff && !shouldSkipImmediateAlert) {
          const orderId = change.doc.id;
          const dataPrint: IOrders = { ...(change.doc.data() as IOrders), id: orderId };
          if (isEnableFalse) {
            showNewOrderAlert(orderId);
            setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
          }
          (async () => {
            try {
              if (Capacitor.getPlatform() !== 'web') {
                const pc = printerContextRef.current;
                if (pc && typeof pc.ensureInitialized === 'function') {
                  await Promise.race([pc.ensureInitialized(), new Promise((res) => setTimeout(res, 2000))]);
                }
              }
            } catch (e) {}

            try {
              const atq = addToQueueRef.current;
              if (atq && typeof atq === 'function') {
                const maxAttempts = 3;
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                  try {
                    await atq({
                      data: dataPrint,
                      ip_printer: dataSettings.ip_printer,
                      dataSettings: dataSettings,
                      stadium: stadiumData,
                      type: 'automatic',
                    });
                    break;
                  } catch (err) {
                    if (attempt === maxAttempts) {
                      console.warn('addToQueue failed after retries', err);
                    } else {
                      await new Promise((r) => setTimeout(r, 300 * attempt));
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('addToQueue failed', err);
            }
          })();
        }
      });
    }

    const allOrders = getMergedOrders();

    if (allOrders.length) {
      lastCreateTime.current = allOrders.reduce((cur, acc) => Math.max(cur, acc.order_create_time.seconds), lastCreateTime.current);
      const riderIds = allOrders
        .flatMap((order) => {
          const ids: string[] = [];
          if (order.rider_id) ids.push(order.rider_id);
          order.multi_orders?.forEach((m: any) => {
            if (m.rider_id) ids.push(m.rider_id);
          });
          return ids;
        })
        .filter((id): id is string => !!id);
      const uniqueNewIds = Array.from(new Set(riderIds)).filter((id) => !riderMap.current[id]);

      if (uniqueNewIds.length > 0) {
        await fetchAndStoreRiderInfo(uniqueNewIds);
      }
    } else {
      lastCreateTime.current = 1;
    }

    // Merge again: the awaits above (rider lookup) give the other listener's handler time to publish its own list.
    setOrderList(getMergedOrders());
    setLoading(false);
  };

  const handleGetAllOrders = async () => {
    ordersByRestaurantId.current = new Map();
    ordersByRestaurantIds.current = new Map();

    const queryByRestaurantId = query(
      getAllOrders(),
      where('restaurant_id', '==', restaurantId),
      where('bill_status', '==', BillStatus.CLEARED),
      orderBy('order_create_time', 'desc')
    );
    const queryByRestaurantIds = query(
      getAllOrders(),
      where('restaurant_ids', 'array-contains', restaurantId),
      where('bill_status', '==', BillStatus.CLEARED),
      orderBy('order_create_time', 'desc')
    );

    const unsubscribeByRestaurantId = onSnapshot(
      queryByRestaurantId,
      (querySnapshot: any) => handleOrdersBranchSnapshot(querySnapshot, ordersByRestaurantId),
      (error: any) => logOrderQueryError('Error fetching orders (restaurant_id):', error)
    );
    const unsubscribeByRestaurantIds = onSnapshot(
      queryByRestaurantIds,
      (querySnapshot: any) => handleOrdersBranchSnapshot(querySnapshot, ordersByRestaurantIds),
      (error: any) => logOrderQueryError('Error fetching orders (restaurant_ids):', error)
    );

    unsubscribeOrdersRef.current = () => {
      unsubscribeByRestaurantId();
      unsubscribeByRestaurantIds();
    };
    return () => unsubscribeOrdersRef.current?.();
  };

  const handleGetAllOrdersPromotion = async () => {
    const ordersQuery = query(
      getAllOrders(),
      where('promotion_restaurant_id', '==', restaurantId),
      where('bill_status', '==', BillStatus.CLEARED),
      orderBy('order_create_time', 'desc')
    );
    unsubscribePromotionOrdersRef.current = onSnapshot(ordersQuery, async (querySnapshot: any) => {
      if (querySnapshot.empty) {
        return;
      }
      const settings = await getDoc(getCurrentSettings(restaurantId));
      const dataSettings = settings.data() as ISettings;
      querySnapshot.docChanges().forEach((change: any) => {
        const data = change.doc.data();
        const isAddedOrder = change.type === 'added';
        const isPreparingOrder = data.order_status === OrderItemStatus.PREPARING;
        const isLastCreateTimeValid = lastPromotionCreateTime.current && lastPromotionCreateTime.current < data.order_create_time.seconds;
        const isEnableFalse = isEnable.current;
        const isCompletePayment = data.bill_status === BillStatus.CLEARED;
        const shouldSkipImmediateAlert = isFuturePreOrder(data);
        if (isAddedOrder && isPreparingOrder && isLastCreateTimeValid && isCompletePayment && isStaff && !shouldSkipImmediateAlert) {
          const orderId = change.doc.id;
          const dataPrint: IOrders = { ...(change.doc.data() as IOrders), id: orderId };
          if (isEnableFalse) {
            showNewOrderAlert(orderId);
            setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
          }
          (async () => {
            try {
              if (Capacitor.getPlatform() !== 'web') {
                const pc = printerContextRef.current;
                if (pc && typeof pc.ensureInitialized === 'function') {
                  await Promise.race([pc.ensureInitialized(), new Promise((res) => setTimeout(res, 2000))]);
                }
              }
            } catch (e) {}

            try {
              const atq = addToQueueRef.current;
              if (atq && typeof atq === 'function') {
                const maxAttempts = 3;
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                  try {
                    await atq({ data: dataPrint, ip_printer: dataSettings.ip_printer, dataSettings: dataSettings, type: 'automatic' });
                    break;
                  } catch (err) {
                    if (attempt === maxAttempts) {
                      console.warn('addToQueue failed after retries', err);
                    } else {
                      await new Promise((r) => setTimeout(r, 300 * attempt));
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('addToQueue failed', err);
            }
          })();
        }
      });
      if (querySnapshot.size) {
        const allOrders: IOrders[] = [];
        querySnapshot.forEach((doc: any) => {
          allOrders.push({ ...(doc.data() as IOrders), id: doc.id, type: 'promotion' });
        });
        lastPromotionCreateTime.current = Object.values(allOrders).reduce((cur, acc) => Math.max(cur, acc.order_create_time.seconds), lastPromotionCreateTime.current);
        setOrderPromotionList(allOrders);
        const riderIds = allOrders.map((order) => order.rider_id).filter((id): id is string => !!id);
        const uniqueNewIds = Array.from(new Set(riderIds)).filter((id) => !riderMap.current[id]);

        if (uniqueNewIds.length > 0) {
          await fetchAndStoreRiderInfo(uniqueNewIds);
        }
      }
    });
    return () => unsubscribePromotionOrdersRef.current?.();
  };

  const unsubscribeAstrolabOrdersRef = useRef<(() => void) | null>(null);

  const handleAstrolabBranchSnapshot = async (querySnapshot: any, storeRef: React.MutableRefObject<Map<string, IOrders>>) => {
    if (!querySnapshot.empty) {
      const settings = await getDoc(getCurrentSettings(adminRestaurantId));
      const dataSettings = settings.data() as ISettings;
      const stadium = await getDoc(doc(getStadiumConfig(), dataSettings.stadium_id));
      const stadiumData = stadium.data() as IStadium;

      querySnapshot.docChanges().forEach((change: any) => {
        const data = change.doc.data();

        if (change.type === 'removed') {
          storeRef.current.delete(change.doc.id);
          return;
        }

        storeRef.current.set(change.doc.id, {
          ...data,
          id: change.doc.id,
        });

        const isAddedOrder = change.type === 'added';
        const isPreparingOrder = data.order_status === OrderItemStatus.PREPARING;
        const isDamagedCreatedOrder = data.order_status === DamagedItemStatus.DAMAGED_CREATED;
        const isCompletePayment = data.bill_status === BillStatus.CLEARED;
        const isLastCreateTimeValid = lastAstrolabCreateTime.current && lastAstrolabCreateTime.current < data.order_create_time.seconds;
        const shouldSkipImmediateAlert = isFuturePreOrder(data);

        if (isAddedOrder && isDamagedCreatedOrder && isLastCreateTimeValid && !shouldSkipImmediateAlert) {
          const orderId = change.doc.id;
          showNewOrderAlert(orderId);
          setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);
        }

        if (isAddedOrder && isPreparingOrder && isLastCreateTimeValid && isCompletePayment && !shouldSkipImmediateAlert) {
          const orderId = change.doc.id;
          const dataPrint: IOrders = { ...(change.doc.data() as IOrders), id: orderId };
          showNewOrderAlert(orderId);
          setNotifications((prev) => [{ isUnread: true, id: orderId }, ...prev]);

          (async () => {
            try {
              if (Capacitor.getPlatform() !== 'web') {
                const pc = printerContextRef.current;
                if (pc && typeof pc.ensureInitialized === 'function') {
                  await Promise.race([pc.ensureInitialized(), new Promise((res) => setTimeout(res, 2000))]);
                }
              }
            } catch (e) {}

            try {
              const atq = addToQueueRef.current;
              if (atq && typeof atq === 'function') {
                const maxAttempts = 3;
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                  try {
                    await atq({
                      data: dataPrint,
                      ip_printer: dataSettings.ip_printer,
                      dataSettings: dataSettings,
                      stadium: stadiumData,
                      type: 'automatic',
                    });
                    break;
                  } catch (err) {
                    if (attempt === maxAttempts) {
                      console.warn('addToQueue failed after retries', err);
                    } else {
                      await new Promise((r) => setTimeout(r, 300 * attempt));
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('addToQueue failed', err);
            }
          })();
        }
      });
    }

    const allOrders: IOrders[] = Array.from(astrolabOrdersByRestaurantId.current.values())
      .concat(Array.from(astrolabOrdersByRestaurantIds.current.values()))
      .sort((a, b) => b.order_create_time.seconds - a.order_create_time.seconds);

    if (allOrders.length) {
      setAstrolabOrderList(allOrders);

      try {
        const riderIds = allOrders.map((order) => order.rider_id).filter((id): id is string => !!id);
        const uniqueNewIds = Array.from(new Set(riderIds)).filter((id) => !riderMap.current[id]);
        if (uniqueNewIds.length > 0) {
          await fetchAndStoreRiderInfo(uniqueNewIds);
        }
      } catch (e) {
        // ignore rider map errors
      }

      lastAstrolabCreateTime.current = allOrders.reduce((cur, acc) => Math.max(cur, acc.order_create_time.seconds), lastAstrolabCreateTime.current);
    }
  };

  const handleGetAstrolabNotifications = async () => {
    if (!adminRestaurantId) return;

    astrolabOrdersByRestaurantId.current = new Map();
    astrolabOrdersByRestaurantIds.current = new Map();

    const queryByRestaurantId = query(
      getAllOrders(),
      where('restaurant_id', '==', adminRestaurantId),
      where('bill_status', '==', BillStatus.CLEARED),
      orderBy('order_create_time', 'desc')
    );
    const queryByRestaurantIds = query(
      getAllOrders(),
      where('restaurant_ids', 'array-contains', adminRestaurantId),
      where('bill_status', '==', BillStatus.CLEARED),
      orderBy('order_create_time', 'desc')
    );

    const unsubscribeByRestaurantId = onSnapshot(
      queryByRestaurantId,
      (querySnapshot: any) => handleAstrolabBranchSnapshot(querySnapshot, astrolabOrdersByRestaurantId),
      (error: any) => logOrderQueryError('Error fetching astrolab orders (restaurant_id):', error)
    );
    const unsubscribeByRestaurantIds = onSnapshot(
      queryByRestaurantIds,
      (querySnapshot: any) => handleAstrolabBranchSnapshot(querySnapshot, astrolabOrdersByRestaurantIds),
      (error: any) => logOrderQueryError('Error fetching astrolab orders (restaurant_ids):', error)
    );

    unsubscribeAstrolabOrdersRef.current = () => {
      unsubscribeByRestaurantId();
      unsubscribeByRestaurantIds();
    };
    return () => unsubscribeAstrolabOrdersRef.current?.();
  };

  const updateOrderStatusDamaged = async (orderId: string, status: string) => {
    try {
      const ordersRef = collection(db, db_config.DB_TABLE_ALL_ORDERS);
      const ordersQuery = query(ordersRef, where('parent_id', '==', orderId));
      const snapshot = await getDocs(ordersQuery);

      if (snapshot.empty) {
        return;
      }

      await Promise.all(
        snapshot.docs.map((docSnapshot: any) =>
          updateOrderDoc(docSnapshot.id, { order_status: status }).catch((error: any) => {
            console.error('Error updating damaged order status:', docSnapshot.id, error);
          })
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  useEffect(() => {
    if (restaurantId || (isSuperAdmin && adminRestaurantId)) {
      lastCreateTime.current = 0;
      lastPromotionCreateTime.current = 0;
      lastAstrolabCreateTime.current = 0;
      fetchData();
      return () => {
        unsubscribeOrdersRef.current?.();
        unsubscribePromotionOrdersRef.current?.();
        unsubscribeAstrolabOrdersRef.current?.();
      };
    }
  }, [restaurantId, adminRestaurantId, isSuperAdmin]);

  // Detach listeners the instant sign-out is detected, rather than waiting for the React render
  // chain (userInfo -> restaurantId -> the effect above) to unwind — Auth.signOut() invalidates
  // the token immediately, so waiting for a re-render leaves a window where these listeners can
  // still receive a permission-denied response for a user that's already signed out.
  useEffect(() => {
    const unsubscribeAuth = Auth.onAuthStateChanged((user) => {
      if (!user) {
        unsubscribeOrdersRef.current?.();
        unsubscribePromotionOrdersRef.current?.();
        unsubscribeAstrolabOrdersRef.current?.();
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const parsedOrderList = useMemo(() => {
    const listAllOrder = (() => {
      const combinedList = [...orderList, ...orderPromotionList];

      const orderMap = new Map<string, IOrders[]>();

      combinedList.forEach((order) => {
        const originalId = order.id.replace(new RegExp(`${damagedPrefix}(_\\d+)?$`), '');

        if (!orderMap.has(originalId)) {
          orderMap.set(originalId, []);
        }

        orderMap.get(originalId)?.push(order);
      });

      return Array.from(orderMap.values())
        .sort((a, b) => b[b.length - 1].order_create_time.seconds - a[a.length - 1].order_create_time.seconds)
        .flat();
    })();

    const today = new Date();
    const startDate = getStartOfDay(today);
    const endDate = getEndOfDay(today);

    const filterOrdersByDate = (orders: IOrders[]) => {
      const ordersOfDay = orders.filter(
        (item) =>
          (new Date(item.order_create_time.seconds * 1000) >= startDate && new Date(item.order_create_time.seconds * 1000) <= endDate) ||
          (item.is_pre_order &&
            item.requested_delivery_time &&
            new Date(item.requested_delivery_time.seconds * 1000) >= startDate &&
            new Date(item.requested_delivery_time.seconds * 1000) <= endDate)
      );

      const orderMap = new Map<string, { damaged: IOrders[]; normal: IOrders[] }>();

      ordersOfDay.forEach((order) => {
        const originalId = order.id.replace(new RegExp(`${damagedPrefix}(_\\d+)?$`), '');

        if (!orderMap.has(originalId)) {
          orderMap.set(originalId, { damaged: [], normal: [] });
        }

        const group = orderMap.get(originalId)!;
        if (order.id.includes(damagedPrefix)) {
          group.damaged.push(order);
        } else {
          group.normal.push(order);
        }
      });

      const sortedGroups: IOrders[][] = [];
      const normalOrders: IOrders[] = [];

      orderMap.forEach(({ damaged, normal }) => {
        damaged.sort((a, b) => b.order_create_time.seconds - a.order_create_time.seconds);
        normal.sort((a, b) => b.order_create_time.seconds - a.order_create_time.seconds);

        if (damaged.length > 0) {
          sortedGroups.push([...damaged, ...normal]);
        } else {
          normalOrders.push(...normal);
        }
      });

      sortedGroups.sort((a, b) => {
        const maxTimeA = a[0]?.order_create_time.seconds || 0;
        const maxTimeB = b[0]?.order_create_time.seconds || 0;
        return maxTimeB - maxTimeA;
      });

      normalOrders.sort((a, b) => b.order_create_time.seconds - a.order_create_time.seconds);

      return [...sortedGroups.flat(), ...normalOrders];
    };

    const allOrders = listAllOrder.filter((item) => item?.foods?.length > 0);
    const allOrdersOfDay = filterOrdersByDate(allOrders).filter(
      (order) =>
        !(
          order.is_pre_order &&
          ([OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED] as string[]).includes(
            getOrderStatusForRestaurant(order, restaurantId, enabledNewVersion)
          )
        )
    );

    const newOrdersOfDay = filterOrdersByStatus(
      [OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED],
      allOrdersOfDay,
      restaurantId
    ).filter((order) => !order.has_damaged_order);

    const readyOrdersOfDay = filterOrdersByStatus([OrderItemStatus.READY_FOR_PICK_UP, OrderItemStatus.DAMAGED_READY_FOR_PICK_UP], allOrdersOfDay, restaurantId).filter(
      (order) => !order.has_damaged_order
    );

    const deliveredOrdersOfDay = filterOrdersByStatus([OrderItemStatus.DELIVERED, DamagedItemStatus.DAMAGED_DELIVERED], allOrdersOfDay, restaurantId);
    const deliveringOrdersOfDay = filterOrdersByStatus(
      [OrderItemStatus.DELIVERING, OrderItemStatus.DAMAGED_DELIVERING, DamagedItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_READY_FOR_PICK_UP],
      allOrdersOfDay,
      restaurantId
    ).filter(
      (order) => !order?.is_damaged || (order?.is_damaged && getOrderStatusForRestaurant(order, restaurantId, enabledNewVersion) === DamagedItemStatus.DAMAGED_DELIVERING)
    );

    const cancelledOrdersOfDay = filterOrdersByStatus([OrderItemStatus.CANCELLED, DamagedItemStatus.DAMAGED_CANCELLED], allOrdersOfDay, restaurantId);

    const allPreOrders = allOrders.filter((item) => item?.is_pre_order);
    const futurePreOrders = allPreOrders.filter((item) => new Date(item.order_create_time.seconds * 1000) >= startDate);

    const preOrdersOfDay = allPreOrders.filter((order) => {
      if (!order.requested_delivery_time) return false;

      const deliveryTime = new Date(order.requested_delivery_time.seconds * 1000);
      const isDeliveryToday = deliveryTime >= startDate && deliveryTime <= endDate;
      const hasValidStatus = ([OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED] as string[]).includes(
        getOrderStatusForRestaurant(order, restaurantId, enabledNewVersion)
      );

      return isDeliveryToday && hasValidStatus;
    });

    return {
      allOrders,
      allOrdersOfDay,
      newOrdersOfDay,
      deliveredOrdersOfDay,
      deliveringOrdersOfDay,
      readyOrdersOfDay,
      cancelledOrdersOfDay,
      allPreOrders,
      futurePreOrders,
      preOrdersOfDay,
    };
  }, [orderList, orderPromotionList, enabledNewVersion, restaurantId]);

  const parsedAstrolabOrderList = useMemo(() => {
    const today = new Date();
    const startDate = getStartOfDay(today);
    const endDate = getEndOfDay(today);

    const allPreOrders = astrolabOrderList.filter((item) => item?.foods?.length > 0 && item?.is_pre_order);

    const preOrdersOfDay = allPreOrders.filter((order) => {
      if (!order.requested_delivery_time) return false;

      const deliveryTime = new Date(order.requested_delivery_time.seconds * 1000);
      const isDeliveryToday = deliveryTime >= startDate && deliveryTime <= endDate;
      const hasValidStatus = ([OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED] as string[]).includes(
        getOrderStatusForRestaurant(order, restaurantId, enabledNewVersion)
      );

      return isDeliveryToday && hasValidStatus;
    });

    return { preOrdersOfDay };
  }, [astrolabOrderList, enabledNewVersion, restaurantId]);

  const { gameSchedules, updateGameSchedule } = useGameScheduleContext();
  const isPrintingRef = useRef(false);
  const printedRef = useRef<Set<string>>(new Set());

  const printedForRestaurant = (schedule: any | undefined, rid?: string) => {
    if (!schedule) return false;
    if (!rid) return false;
    const arr: string[] | undefined = schedule.preorders_printed_restaurants;
    if (!arr || !Array.isArray(arr)) return false;
    return arr.includes(rid);
  };

  const printPreOrders = useCallback(
    async (dateStr: string, targetRestaurantId?: string) => {
      const rid = targetRestaurantId || restaurantId;
      if (!rid) return;
      const key = `${dateStr}__${rid}`;
      if (printedRef.current.has(key)) return;
      if (isPrintingRef.current) return;
      isPrintingRef.current = true;
      printedRef.current.add(key);
      try {
        const todaySchedule = gameSchedules.find((s) => s.date === dateStr);
        if (!todaySchedule || printedForRestaurant(todaySchedule, rid)) {
          return;
        }

        const sourcePreOrders = isSuperAdmin && targetRestaurantId && targetRestaurantId === adminRestaurantId ? parsedAstrolabOrderList.preOrdersOfDay : parsedOrderList.preOrdersOfDay;
    
        const preOrders = sourcePreOrders.filter((order) => {
          const orderDate = dayjs(order.requested_delivery_time!.seconds * 1000).format('YYYY-MM-DD');
          if (orderDate !== dateStr) return false;
          const status = order.order_status;
          return [OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED].includes(status as any);
        });

        if (preOrders.length === 0) {
          return;
        }

        const settings = await getDoc(getCurrentSettings(rid));
        const dataSettings = settings.data() as ISettings;
        const stadium = await getDoc(doc(getStadiumConfig(), dataSettings.stadium_id));
        const stadiumData = stadium.data() as IStadium;

        let printed = 0;
        for (const order of preOrders) {
          try {
            addToQueue({
              data: order,
              ip_printer: dataSettings.ip_printer,
              dataSettings: dataSettings,
              stadium: stadiumData,
              type: 'automatic',
            });
            printed++;
            await new Promise((resolve) => setTimeout(resolve, 100));
          } catch (error) {
            console.error(`[AutoPrint] Failed to queue order ${order.id}:`, error);
          }
        }

        if (printed > 0) {
          try {
            const ridToMark = targetRestaurantId || restaurantId;
            const existing: string[] = (todaySchedule && (todaySchedule.preorders_printed_restaurants || [])) || [];
            if (!existing.includes(ridToMark)) {
              await updateGameSchedule(dateStr, { preorders_printed_restaurants: [...existing, ridToMark] });
            }
          } catch (error) {
            console.error('[AutoPrint] Failed to update DB:', error);
          }
        }
      } finally {
        isPrintingRef.current = false;
      }
    },
    [gameSchedules, parsedOrderList.preOrdersOfDay, parsedAstrolabOrderList.preOrdersOfDay, addToQueue, updateGameSchedule, restaurantId]
  );

  useEffect(() => {
    const isStaffUser = userInfo?.role === ROLES.STAFF;
    const isSuperUser = isSuperAdmin;

    if (!(isStaffUser || isSuperUser)) return;

    const activeRestaurantId = isSuperUser ? adminRestaurantId : restaurantId;
    if (!activeRestaurantId) return;

    const today = dayjs().format('YYYY-MM-DD');
    const todaySchedule = gameSchedules.find((s) => s.date === today);

    if (!todaySchedule || printedForRestaurant(todaySchedule, activeRestaurantId)) {
      return;
    }

    printPreOrders(today, activeRestaurantId);
  }, [gameSchedules, parsedOrderList.preOrdersOfDay, parsedAstrolabOrderList.preOrdersOfDay, printPreOrders, restaurantId, adminRestaurantId, isSuperAdmin, userInfo?.role]);

  useEffect(() => {
    const loadSeats = async () => {
      const stadiumId = userInfo?.stadium_id ?? stadium_data?.id;
      if (stadiumId && parsedOrderList.allOrders.length > 0) {
        const uniqueTableNumbers = Array.from(new Set(parsedOrderList.allOrders.map((order) => order.order_table_number).filter(Boolean)));
        const seats = await fetchMultipleSeatsByTableNumbers(stadiumId, uniqueTableNumbers, enabledNewVersion);
        setSeatDataMap(seats);
      }
    };
    loadSeats();
  }, [parsedOrderList.allOrders, userInfo?.stadium_id, enabledNewVersion, stadium_data?.id]);

  const information = (item: IOrders) => getSeatInformation(item, seatDataMap);

  const ordersWithSeatInfo = useMemo(() => {
    const populateSeatNumber = (orders: IOrders[]): IOrders[] => {
      return orders.map((order) => {
        const getRiderIdForRestaurant = () => {
          if (order.multi_orders && Array.isArray(order.multi_orders) && restaurantId) {
            const mo = order.multi_orders.find((m: any) => m.restaurant_id === restaurantId);
            if (mo && mo.rider_id) return mo.rider_id;
          }
          return order.rider_id;
        };

        const riderId = getRiderIdForRestaurant();

        return {
          ...order,
          seat_number: formatSeatInfo(information(order)),
          rider_info: riderId ? riderInfos[riderId] : undefined,
        } as IOrders;
      });
    };

    return {
      allOrders: populateSeatNumber(parsedOrderList.allOrders),
      allOrdersOfDay: populateSeatNumber(parsedOrderList.allOrdersOfDay),
      newOrdersOfDay: populateSeatNumber(parsedOrderList.newOrdersOfDay),
      readyOrdersOfDay: populateSeatNumber(parsedOrderList.readyOrdersOfDay),
      deliveredOrdersOfDay: populateSeatNumber(parsedOrderList.deliveredOrdersOfDay),
      deliveringOrdersOfDay: populateSeatNumber(parsedOrderList.deliveringOrdersOfDay),
      cancelledOrdersOfDay: populateSeatNumber(parsedOrderList.cancelledOrdersOfDay),
      allPreOrders: populateSeatNumber(parsedOrderList.allPreOrders),
      futurePreOrders: populateSeatNumber(parsedOrderList.futurePreOrders),
      preOrdersOfDay: populateSeatNumber(parsedOrderList.preOrdersOfDay),
    };
  }, [parsedOrderList, seatDataMap, riderInfos]);

  return (
    <OrderContext.Provider
      value={{
        loading,
        allOrders: ordersWithSeatInfo.allOrders,
        allOrdersOfDay: ordersWithSeatInfo.allOrdersOfDay,
        newOrdersOfDay: ordersWithSeatInfo.newOrdersOfDay,
        readyOrdersOfDay: ordersWithSeatInfo.readyOrdersOfDay,
        deliveredOrdersOfDay: ordersWithSeatInfo.deliveredOrdersOfDay,
        deliveringOrdersOfDay: ordersWithSeatInfo.deliveringOrdersOfDay,
        cancelledOrdersOfDay: ordersWithSeatInfo.cancelledOrdersOfDay,
        allPreOrders: ordersWithSeatInfo.allPreOrders,
        futurePreOrders: ordersWithSeatInfo.futurePreOrders,
        preOrdersOfDay: ordersWithSeatInfo.preOrdersOfDay,
        updateOrderStatusDamaged,
        riderMap: riderMap.current,
        riderInfos,
        seatDataMap,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

const useOrderContext = () => {
  const context = useContext(OrderContext);
  return context;
};

export { OrderProvider, useOrderContext };
